/**
 * Local tag guard (F4.6-c, F4.7, F4.1-j): the phone refuses a code of the wrong kind, a code not
 * issued to this operator, a code from a roll not confirmed for the day, a voided code, a code
 * already bound, or a code already on another bag of this delivery. Pure functions of the roster
 * rolls and the phone's own sets, so every rule is unit-tested.
 *
 * A code is `{payload_prefix}{sequence}`, e.g. RJ-BELU-017 (prefix "RJ-BELU-", sequence 17).
 */
import type { RefusalReason } from '../commands'

export type CodeKind = 'BAG' | 'SEAL'

export interface GuardRoll {
  batch_id: string
  kind: string
  start_sequence: number
  end_sequence: number
  payload_prefix: string
  sequence_digits?: number | null
  /** Every code of the roll in sequence order, when the roster sends them (authoritative). */
  payloads?: readonly string[]
  issued_to_id?: string | null
  /** V1: issued to the village by the office (false = printed for it, not issued yet). */
  issued?: boolean | null
  bound_payloads?: readonly string[]
  voided_payloads?: readonly string[]
}

export interface ParsedCode {
  roll: GuardRoll
  sequence: number
}

/** Find the roll a code belongs to (prefix + digits within the roll's range). */
export function parseCode(code: string, rolls: readonly GuardRoll[]): ParsedCode | null {
  const c = code.trim().toUpperCase()
  for (const roll of rolls) {
    // The server's list wins: printed codes need not be prefix + plain number (…SA999 → …SB000).
    if (roll.payloads?.length) {
      const i = roll.payloads.findIndex((p) => p.toUpperCase() === c)
      if (i >= 0) return { roll, sequence: roll.start_sequence + i }
      continue
    }
    const prefix = roll.payload_prefix.toUpperCase()
    if (!c.startsWith(prefix)) continue
    const rest = c.slice(prefix.length)
    if (!/^\d+$/.test(rest)) continue
    const sequence = Number(rest)
    if (sequence >= roll.start_sequence && sequence <= roll.end_sequence) return { roll, sequence }
  }
  return null
}

export function formatCode(roll: GuardRoll, sequence: number): string {
  const listed = roll.payloads?.[sequence - roll.start_sequence]
  if (listed) return listed
  const digits = roll.sequence_digits ?? 0
  return `${roll.payload_prefix}${String(sequence).padStart(digits, '0')}`
}

/**
 * 'Type number' (F4.6): a bare number typed from the tag is expanded with the prefix of the
 * confirmed roll of the right kind that contains it. Anything else is returned upper-cased.
 */
export function expandTypedCode(
  raw: string,
  kind: CodeKind,
  confirmedRolls: readonly GuardRoll[],
): string {
  const c = raw.trim().toUpperCase()
  if (!/^\d+$/.test(c)) return c
  const n = Number(c)
  const roll = confirmedRolls.find(
    (r) => r.kind === kind && n >= r.start_sequence && n <= r.end_sequence,
  )
  return roll ? formatCode(roll, n) : c
}

export interface GuardContext {
  kind: CodeKind
  /** The session's operator — rolls must be issued to her (or confirmed today by a supervisor). */
  operatorId: string
  rolls: readonly GuardRoll[]
  /**
   * Batches confirmed for this session (confirm_roll), incl. a supervisor's on-the-spot spare.
   * V1 (`byVillage`): every roll the office issued to the village.
   */
  confirmedBatchIds: ReadonlySet<string>
  /** V1: rolls belong to the village — no operator match, no confirm scan. */
  byVillage?: boolean
  /** Codes bound on this phone already (earlier deliveries, any session). */
  boundPayloads: ReadonlySet<string>
  /** Codes voided on this phone already. */
  voidedPayloads: ReadonlySet<string>
  /** Codes already used in THIS delivery draft (bags and seal). */
  inDraft: ReadonlySet<string>
  /** Codes on the other farmers open on this phone (code → that farmer's name). */
  otherDrafts?: ReadonlyMap<string, string>
}

export type GuardResult =
  | { ok: true; payload: string; batch_id: string; sequence: number }
  | { ok: false; payload: string; reason: RefusalReason; otherFarmer?: string }

/** Check a scanned or typed code for this screen. Order matters: the most specific reason wins. */
export function checkCode(raw: string, ctx: GuardContext): GuardResult {
  const payload = raw.trim().toUpperCase()
  const parsed = parseCode(payload, ctx.rolls)
  if (!parsed) return { ok: false, payload, reason: 'TAG_UNKNOWN' }
  const { roll, sequence } = parsed
  if (roll.kind !== ctx.kind) return { ok: false, payload, reason: 'TAG_WRONG_KIND' }
  const confirmed = ctx.confirmedBatchIds.has(roll.batch_id)
  // V1: a roll of the village the office has not issued yet is not ours to use.
  if (!confirmed && (ctx.byVillage || roll.issued_to_id !== ctx.operatorId)) {
    return { ok: false, payload, reason: 'TAG_UNKNOWN' }
  }
  if (ctx.voidedPayloads.has(payload) || (roll.voided_payloads ?? []).includes(payload)) {
    return { ok: false, payload, reason: 'TAG_VOID' }
  }
  if (ctx.inDraft.has(payload)) return { ok: false, payload, reason: 'TAG_DUPLICATE_SCAN' }
  // Several farmers open at once: a code on another open farmer's bag or sample is his, not ours.
  const other = ctx.otherDrafts?.get(payload)
  if (other !== undefined) {
    return { ok: false, payload, reason: 'TAG_DUPLICATE_SCAN', otherFarmer: other }
  }
  if (ctx.boundPayloads.has(payload) || (roll.bound_payloads ?? []).includes(payload)) {
    return { ok: false, payload, reason: 'TAG_ALREADY_USED' }
  }
  if (!confirmed) return { ok: false, payload, reason: 'ROLL_NOT_CONFIRMED' }
  return { ok: true, payload, batch_id: roll.batch_id, sequence }
}

// ---------------------------------------------------------------------------------------------
// Roll confirmation (F4.1): first and last code of a roll or strip
// ---------------------------------------------------------------------------------------------

export type RollScanProblem =
  | 'UNKNOWN' // not a roll of this village
  | 'WRONG_KIND' // a seal scanned as a tag, or the reverse
  | 'DIFFERENT_ROLLS' // first and last from different rolls
  | 'ORDER' // last before first
  | 'NOT_ASSIGNED' // a roll of this village, not assigned to her — supervisor can assign on the spot

export type RollScanResult =
  | { ok: true; roll: GuardRoll; first: number; last: number; count: number }
  | { ok: false; problem: RollScanProblem; roll?: GuardRoll }

/** Check one scanned end (first or last) of a roll. */
export function checkRollEnd(
  raw: string,
  kind: CodeKind,
  rolls: readonly GuardRoll[],
):
  | { ok: true; roll: GuardRoll; sequence: number }
  | { ok: false; problem: 'UNKNOWN' | 'WRONG_KIND' } {
  const parsed = parseCode(raw, rolls)
  if (!parsed) return { ok: false, problem: 'UNKNOWN' }
  if (parsed.roll.kind !== kind) return { ok: false, problem: 'WRONG_KIND' }
  return { ok: true, roll: parsed.roll, sequence: parsed.sequence }
}

/**
 * Check the first and last code together. `supervisorOverride` = a supervisor is assigning an
 * unassigned (or another operator's) roll to this operator on the spot.
 */
export function checkRollPair(
  first: string,
  last: string,
  kind: CodeKind,
  operatorId: string,
  rolls: readonly GuardRoll[],
  supervisorOverride = false,
): RollScanResult {
  const a = checkRollEnd(first, kind, rolls)
  if (!a.ok) return { ok: false, problem: a.problem }
  const b = checkRollEnd(last, kind, rolls)
  if (!b.ok) return { ok: false, problem: b.problem }
  if (a.roll.batch_id !== b.roll.batch_id) return { ok: false, problem: 'DIFFERENT_ROLLS' }
  if (b.sequence < a.sequence) return { ok: false, problem: 'ORDER', roll: a.roll }
  if (a.roll.issued_to_id !== operatorId && !supervisorOverride) {
    return { ok: false, problem: 'NOT_ASSIGNED', roll: a.roll }
  }
  return {
    ok: true,
    roll: a.roll,
    first: a.sequence,
    last: b.sequence,
    count: b.sequence - a.sequence + 1,
  }
}

/** Codes of [first, last] not yet bound or voided anywhere we know of: "in hand". */
export function codesInHand(
  roll: GuardRoll,
  first: number,
  last: number,
  used: Iterable<string>,
): number {
  const seqs = new Set<number>()
  for (const p of [...used, ...(roll.bound_payloads ?? []), ...(roll.voided_payloads ?? [])]) {
    const parsed = parseCode(p, [roll])
    if (parsed) seqs.add(parsed.sequence)
  }
  let n = 0
  for (let s = first; s <= last; s++) if (!seqs.has(s)) n++
  return n
}
